import React, { useCallback, useId, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import type { DateRange as RDPRange } from 'react-day-picker';
import './DateRangePicker.scss';
import type { DateRangePickerProps, Preset } from './DateRangePicker.types';
import {
  PRESET_LABELS,
  detectPreset,
  formatRangeLabel,
  getPresetRange,
  rangeToTuple,
  tupleToRange,
} from './DateRangePicker.utils';
import { useOutsideDismiss } from './useOutsideDismiss';

const ROOT = 'date-range-picker';

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  mode = 'range',
  value,
  onChange,
  presets = [],
  minDate,
  maxDate,
  disabled = false,
  className = '',
  hasError = false,
  errorMessage,
  ariaLabel,
  dropdownDirection = 'auto',
  id,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [resolvedDirection, setResolvedDirection] = useState<'down' | 'up'>('down');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const reactId = useId();
  const labelSpanId = `${ROOT}-selection-${reactId}`;
  const ariaLabelId = ariaLabel ? `${ROOT}-label-${reactId}` : undefined;
  const labelledBy = [ariaLabelId, labelSpanId].filter(Boolean).join(' ') || undefined;

  const close = useCallback(() => setIsOpen(false), []);
  useOutsideDismiss(containerRef, isOpen, close);

  useLayoutEffect(() => {
    if (!isOpen) return;
    if (dropdownDirection !== 'auto') {
      setResolvedDirection(dropdownDirection);
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedPopoverHeight = mode === 'range' ? 380 : 340;
    setResolvedDirection(spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow ? 'up' : 'down');
  }, [isOpen, dropdownDirection, mode]);

  const selectedPreset = useMemo(() => detectPreset(value, presets), [value, presets]);
  const label = useMemo(
    () =>
      value && ((value[0] ?? value[1]) || (value as unknown[])[3] === 'allTime')
        ? formatRangeLabel(value, mode, presets)
        : placeholder ?? formatRangeLabel(value, mode, presets),
    [value, mode, presets, placeholder],
  );

  const handleTriggerClick = () => {
    if (disabled) return;
    setIsOpen((v) => !v);
  };

  const handlePresetClick = (preset: Preset) => {
    if (preset === 'custom') {
      const [start, end] = value as [Date | null, Date | null];
      onChange([start, end]);
      return;
    }
    const next = getPresetRange(preset);
    onChange(next);
    close();
    triggerRef.current?.focus();
  };

  const handleRangeSelect = (range: RDPRange | undefined) => {
    const from = range?.from;
    const to = range?.to;
    onChange(rangeToTuple(from, to));
    if (from && to) {
      close();
      triggerRef.current?.focus();
    }
  };

  const handleSingleSelect = (day: Date | undefined) => {
    if (!day) {
      onChange([null, null]);
      return;
    }
    onChange([day, day]);
    close();
    triggerRef.current?.focus();
  };

  const rangeSelected = tupleToRange(value);
  const singleSelected = (value as [Date | null, Date | null])[0] ?? undefined;
  const defaultMonth = rangeSelected.from ?? singleSelected ?? new Date();

  const popoverClasses = [
    `${ROOT}__popover`,
    resolvedDirection === 'up' ? `${ROOT}__popover--up` : `${ROOT}__popover--down`,
  ].join(' ');

  const rootClasses = [ROOT, className, disabled && `${ROOT}--disabled`, hasError && `${ROOT}--error`]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses} ref={containerRef}>
      {ariaLabel && (
        <span id={ariaLabelId} className={`${ROOT}__sr-only`}>
          {ariaLabel}
        </span>
      )}
      <button
        type="button"
        ref={triggerRef}
        className={`${ROOT}__trigger`}
        onClick={handleTriggerClick}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-labelledby={labelledBy}
        id={id}
      >
        <span id={labelSpanId} className={`${ROOT}__trigger-label`}>
          {label}
        </span>
        <span className={`${ROOT}__trigger-caret`} aria-hidden="true">
          ▾
        </span>
      </button>

      {isOpen && !disabled && (
        <div className={popoverClasses} role="dialog" aria-label={ariaLabel ?? 'Date range picker'}>
          {presets.length > 0 && (
            <ul className={`${ROOT}__presets`} role="listbox">
              {presets.map((preset) => {
                const isActive = preset === selectedPreset || (preset === 'custom' && !selectedPreset);
                return (
                  <li key={preset} role="none">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`${ROOT}__preset ${isActive ? `${ROOT}__preset--active` : ''}`}
                      onClick={() => handlePresetClick(preset)}
                    >
                      {PRESET_LABELS[preset]}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className={`${ROOT}__calendar`}>
            {mode === 'range' ? (
              <DayPicker
                mode="range"
                numberOfMonths={2}
                selected={rangeSelected}
                onSelect={handleRangeSelect}
                defaultMonth={defaultMonth}
                disabled={
                  minDate || maxDate
                    ? ([minDate ? { before: minDate } : false, maxDate ? { after: maxDate } : false].filter(
                        Boolean,
                      ) as never)
                    : undefined
                }
                startMonth={minDate}
                endMonth={maxDate}
              />
            ) : (
              <DayPicker
                mode="single"
                selected={singleSelected}
                onSelect={handleSingleSelect}
                defaultMonth={defaultMonth}
                disabled={
                  minDate || maxDate
                    ? ([minDate ? { before: minDate } : false, maxDate ? { after: maxDate } : false].filter(
                        Boolean,
                      ) as never)
                    : undefined
                }
                startMonth={minDate}
                endMonth={maxDate}
              />
            )}
          </div>
        </div>
      )}

      {hasError && errorMessage && <div className={`${ROOT}__error-message`}>{errorMessage}</div>}
    </div>
  );
};

export default DateRangePicker;
